import { FormPickerDatas } from '../local-types';

const dateFormat = require('dateformat');
dateFormat.i18n = {
    dayNames: [
        'Dim',
        'Lun',
        'Mar',
        'Mer',
        'Jeu',
        'Ven',
        'Sam',
        'Dimanche',
        'Lundi',
        'Mardi',
        'Mercredi',
        'Jeudi',
        'Vendredi',
        'Samedi',
    ],
    monthNames: [
        'Jan',
        'Fev',
        'Mar',
        'Avr',
        'Mai',
        'Jui',
        'Juil',
        'Aou',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
        'Janvier',
        'Février',
        'Mars',
        'Avril',
        'Mai',
        'Juin',
        'Juillet',
        'Août',
        'Septembre',
        'Octobre',
        'Novembre',
        'Décembre',
    ],
    timeNames: ['a', 'p', 'am', 'pm', 'A', 'P', 'AM', 'PM'],
};

interface Day {
    id: string;
    label: string;
}
export default class DateUtils {
    static tsToDate(timestamp: number, short: boolean = false): string {
        if (!timestamp) {
            return '-';
        }

        let date;
        if (typeof timestamp === 'number') {
            date = new Date(timestamp * 1000);
        } else if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else {
            return `Type ${typeof timestamp} non géré`;
        }

        return date.toLocaleDateString('fr-FR', short ? {} : { hour: 'numeric', minute: 'numeric', second: 'numeric' });
    }

    static stringToTs(date: string): number {
        if (!date) {
            return -1;
        }
        const res = new Date(date).getTime();
        return res / 1000;
    }

    static dateToSQlDateTime(date: Date): string {
        return date.toISOString().slice(0, 19).replace('T', ' ');
    }

    /**
     * Converts a UTC datetime string into a local datetime string
     * formatted for HTML <input type="datetime-local">.
     * It applies the browser's timezone offset to show the correct local time.
     */
    static toLocalDatetimeInput(value: string): string {
        const date = new Date(value); // valeur UTC
        const tzOffset = date.getTimezoneOffset() * 60000; // en ms
        const localISO = new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
        return localISO;
    }

    static stringToDate(date: string, short: boolean = true): string {
        return this.tsToDate(this.stringToTs(date), short);
    }

    static dateToString(date: Date, full: boolean = false): string {
        if (!date) {
            return 'Date Invalid';
        }
        const base =
            date.getFullYear() +
            '-' +
            ('00' + (date.getMonth() + 1)).slice(-2) +
            '-' +
            ('00' + date.getDate()).slice(-2);
        return `${base}${
            full
                ? ' ' +
                  ('00' + date.getHours()).slice(-2) +
                  ':' +
                  ('00' + date.getMinutes()).slice(-2) +
                  ':' +
                  ('00' + date.getSeconds()).slice(-2)
                : ''
        }`;
    }

    static dateFormat(...params) {
        return dateFormat(...params);
    }

    static addYears(years: number, date: Date = new Date()) {
        date.setFullYear(date.getFullYear() + years);
        return date;
    }

    static generateMonthsPicker = (): FormPickerDatas[] => {
        const months = [...Array(12).keys()];
        return months.map((m) => {
            const month = '' + (m + 1);
            return { id: month, name: month };
        });
    };

    static generateYearsPicker = (interval: number = 8): FormPickerDatas[] => {
        const now = new Date().getFullYear();
        const months = [...Array(interval).keys()];
        return months.map((m) => {
            const year = '' + (now + m);
            return { id: year, name: year };
        });
    };

    static dateDiff(
        date1,
        date2,
        except?: { day?: boolean; hour?: boolean; min?: boolean; sec?: boolean },
    ): { day: number; hour: number; min: number; sec: number } {
        const diff: any = {};
        let tmp = date2 - date1;

        tmp = Math.floor(tmp / 1000); // Nombre de secondes entre les 2 dates
        diff.sec = tmp % 60; // Extraction du nombre de secondes

        tmp = Math.floor((tmp - diff.sec) / 60); // Nombre de minutes (partie entière)
        diff.min = tmp % 60; // Extraction du nombre de minutes

        tmp = Math.floor((tmp - diff.min) / 60); // Nombre d'heures (entières)
        diff.hour = tmp % 24; // Extraction du nombre d'heures

        tmp = Math.floor((tmp - diff.hour) / 24); // Nombre de jours restants
        diff.day = tmp;

        if (except) {
            Object.keys(except).forEach((key) => {
                if (except[key]) {
                    diff[key] = 0;
                }
            });
        }

        return diff;
    }

    static getFullDiff(t, diff: { day: number; hour: number; min: number; sec: number }): string {
        let res = '';
        if (diff.day) {
            res += `${diff.day} ${t('common:day')} `;
        }
        if (diff.hour) {
            res += `${diff.hour} ${t('common:hour')} `;
        }
        if (diff.min) {
            res += `${diff.min} ${t('common:min')} `;
        }
        if (diff.sec) {
            res += `${diff.sec} ${t('common:sec')} `;
        }
        return res.trimEnd();
    }

    static getBiggestDiff(t, diff: { day: number; hour: number; min: number; sec: number }): string {
        if (diff.day) {
            return `${diff.day} ${t('common:day')}`;
        } else if (diff.hour) {
            return `${diff.hour} ${t('common:hour')}`;
        } else if (diff.min) {
            return `${diff.min} ${t('common:min')}`;
        } else {
            return `${diff.sec} ${t('common:sec')}`;
        }
    }

    static getPreviousMonth(date = new Date()) {
        const previous = new Date(date.getTime());
        previous.setDate(0); // 0 will result in the last day of the previous month
        previous.setDate(1); // 1 will result in the first day of the month
        return previous;
    }

    static getNextMonth(now = new Date()) {
        let current;
        if (now.getMonth() === 11) {
            current = new Date(now.getFullYear() + 1, 0, 1);
        } else {
            current = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        }
        return current;
    }

    static getPreviousWeek(now = new Date()) {
        return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    }

    static getNextWeek(now = new Date()) {
        return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
    }

    static getNextDate(diffFromToday: number, now = new Date()) {
        return new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffFromToday);
    }

    static getMonday(now = new Date()) {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        return new Date(now.setDate(diff));
    }

    static generateDays = (t): Day[] => {
        const days: Day[] = [
            { id: '1', label: t('common:days.monday') },
            { id: '2', label: t('common:days.tuesday') },
            { id: '3', label: t('common:days.wednesday') },
            { id: '4', label: t('common:days.thursday') },
            { id: '5', label: t('common:days.friday') },
            { id: '6', label: t('common:days.saturday') },
            { id: '0', label: t('common:days.sunday') },
        ];
        return days;
    };

    static todayAtMidnight(): Date {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }

    static sortingDaysArr: string[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
}
